import React, { useState, useEffect } from 'react';
import { Button, Card, Spinner, useToast, Modal } from '@aidkitorg/component-library';
import { Form } from 'react-bootstrap';
import { usePost } from '../API';

interface EnabledSurvey {
  createdTime: string;
  surveyName: string;
  url: string;
  kind: 'subsurvey' | 'inline';
  path?: string;
  needs_key?: boolean;
}

interface EnabledSurveysPageProps {}

export const EnabledSurveysPage: React.FC<EnabledSurveysPageProps> = () => {
  const [enabledSurveys, setEnabledSurveysState] = useState<EnabledSurvey[]>([]);
  const [availableSurveys, setAvailableSurveys] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingSurvey, setEditingSurvey] = useState<EnabledSurvey | null>(null);
  const [formData, setFormData] = useState<Partial<EnabledSurvey>>({});
  const { toast } = useToast();

  // API hooks
  const getEnabledSurveys = usePost('/survey/get_enabled_surveys');
  const setEnabledSurveysApi = usePost('/survey/set_enabled_surveys');
  const listSurveys = usePost('/survey/list_surveys');

  // Helper function to extract survey name from full format (dso-program-surveyname.json)
  const extractSurveyName = (fullName: string): string => {
    // Remove .json extension if present
    const withoutExtension = fullName.replace(/\.json$/, '');
    // Split by '-' and get the last part (survey name)
    const parts = withoutExtension.split('-');
    return parts.length >= 3 ? parts.slice(2).join('-') : withoutExtension;
  };

  // Computed disabled surveys (available but not enabled)
  const enabledSurveyNames = enabledSurveys.map(s => s.surveyName);
  const disabledSurveys = availableSurveys
    .map(extractSurveyName)
    .filter(survey => !enabledSurveyNames.includes(survey));

  // Load enabled surveys and available surveys
  const loadData = async () => {
    try {
      setLoading(true);

      // Load enabled surveys
      const enabledResult = await getEnabledSurveys({});
      if (Array.isArray(enabledResult)) {
        setEnabledSurveysState(enabledResult);
      } else {
        setEnabledSurveysState([]);
      }

      // Load available surveys
      const availableResult = await listSurveys({});
      if (Array.isArray(availableResult)) {
        setAvailableSurveys(availableResult.filter((s): s is string => s != null));
      } else {
        setAvailableSurveys([]);
      }

    } catch (err) {
      console.error('Failed to load surveys:', err);
      toast({
        variant: 'error',
        title: 'Failed to load surveys',
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setLoading(false);
    }
  };

  // Open modal to add/edit a survey
  const openEditModal = (survey?: EnabledSurvey, surveyName?: string) => {
    if (survey) {
      setEditingSurvey(survey);
      setFormData(survey);
    } else if (surveyName) {
      setEditingSurvey(null);
      const programName = window.location.hostname.includes('localhost') ? 'postgres' : 'production';
      setFormData({
        surveyName,
        url: `dso-${programName}-${surveyName}`,
        kind: 'inline',
      });
    } else {
      setEditingSurvey(null);
      setFormData({});
    }
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setEditingSurvey(null);
    setFormData({});
  };

  // Save survey from modal
  const handleSaveFromModal = async () => {
    if (!formData.surveyName || !formData.url || !formData.kind) {
      toast({
        variant: 'error',
        title: 'Required fields missing',
        description: 'Survey name, URL, and kind are required',
      });
      return;
    }

    const surveyToSave: EnabledSurvey = {
      createdTime: editingSurvey?.createdTime || new Date().toISOString(),
      surveyName: formData.surveyName,
      url: formData.url,
      kind: formData.kind,
      path: formData.path,
      needs_key: formData.needs_key,
    };

    let updatedSurveys: EnabledSurvey[];
    if (editingSurvey) {
      // Update existing
      updatedSurveys = enabledSurveys.map(s => 
        s.surveyName === editingSurvey.surveyName ? surveyToSave : s
      );
    } else {
      // Add new
      if (enabledSurveyNames.includes(surveyToSave.surveyName)) {
        toast({
          variant: 'error',
          title: 'Survey already enabled',
        });
        return;
      }
      updatedSurveys = [...enabledSurveys, surveyToSave];
    }

    await saveEnabledSurveys(updatedSurveys);
    closeEditModal();
  };

  // Disable a survey
  const disableSurvey = async (surveyName: string) => {
    const updatedSurveys = enabledSurveys.filter(s => s.surveyName !== surveyName);
    await saveEnabledSurveys(updatedSurveys);
  };

  // Save enabled surveys
  const saveEnabledSurveys = async (updatedSurveys: EnabledSurvey[]) => {
    try {
      setSaving(true);

      await setEnabledSurveysApi({ enabledSurveys: updatedSurveys });
      setEnabledSurveysState(updatedSurveys);
      
      toast({
        variant: 'success',
        title: 'Survey status updated successfully',
      });

    } catch (err) {
      console.error('Failed to update survey status:', err);
      toast({
        variant: 'error',
        title: 'Failed to update survey status',
        description: err instanceof Error ? err.message : undefined,
      });
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <Spinner size="large" />
        <span className="ml-3">Loading enabled surveys...</span>
      </div>
    );
  }

  return (
    <div className="container-fluid max-w-4xl mx-auto">
      <div className="row">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div>
              <h3>Enabled Surveys</h3>
              <p className="text-muted">Manage which surveys are enabled for this program</p>
            </div>
            <Button onClick={loadData} variant="secondary" disabled={loading}>
              {loading ? <Spinner size="small" /> : 'Refresh'}
            </Button>
          </div>

          {/* Enabled Surveys Section */}
          <Card className="p-4 mb-4">
            <div className="mb-3">
              <h5 className="d-flex align-items-center">
                <span className="text-success mr-2">●</span>
                Enabled Surveys ({enabledSurveys.length})
              </h5>
            </div>
            {enabledSurveys.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">No surveys are currently enabled</p>
              </div>
            ) : (
              <div className="row">
                {enabledSurveys.map((survey) => {
                  const isEntireProgram = survey.surveyName === 'entireprogram';
                  return (
                    <div key={survey.surveyName} className="col-12 mb-3">
                      <div className="p-3 border rounded">
                        <div className="d-flex justify-content-between align-items-start mb-2">
                          <div className="flex-grow-1">
                            <div className="d-flex align-items-center mb-1">
                              <span className="font-weight-bold">{survey.surveyName}</span>
                              {isEntireProgram && (
                                <span className="badge badge-secondary ml-2" style={{ fontSize: '0.75em' }}>
                                  required
                                </span>
                              )}
                              <span className="badge badge-info ml-2" style={{ fontSize: '0.75em' }}>
                                {survey.kind}
                              </span>
                            </div>
                            <div className="text-muted small">
                              <div><strong>URL:</strong> {survey.url}</div>
                              {survey.path && <div><strong>Path:</strong> {survey.path}</div>}
                              {'needs_key' in survey && <div><strong>Needs Key:</strong> {String(survey.needs_key)}</div>}
                              <div className="text-muted" style={{ fontSize: '0.85em' }}>
                                Created: {new Date(survey.createdTime).toLocaleString()}
                              </div>
                            </div>
                          </div>
                          {!isEntireProgram && (
                            <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0 }}>
                              <Button
                                variant="link"
                                size="sm"
                                onClick={() => openEditModal(survey)}
                                disabled={saving}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="secondary"
                                size="sm"
                                onClick={() => disableSurvey(survey.surveyName)}
                                disabled={saving}
                              >
                                {saving ? <Spinner size="small" /> : 'Disable'}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          {/* Disabled Surveys Section */}
          <Card className="p-4">
            <div className="mb-3">
              <h5 className="d-flex align-items-center">
                <span className="text-muted mr-2">●</span>
                Available Surveys ({disabledSurveys.length})
              </h5>
            </div>
            {disabledSurveys.length === 0 ? (
              <div className="text-center py-4">
                <p className="text-muted">All available surveys are enabled</p>
              </div>
            ) : (
              <div className="row">
                {disabledSurveys.map((surveyName) => (
                  <div key={surveyName} className="col-12 mb-2">
                    <div className="d-flex justify-content-between align-items-center p-3 border rounded">
                      <div>
                        <span className="font-weight-medium">{surveyName}</span>
                      </div>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openEditModal(undefined, surveyName)}
                        disabled={saving}
                      >
                        Enable
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          <div className="mt-4">
            <Card className="p-4">
              <h5>About Enabled Surveys</h5>
              <p className="text-muted mb-3">
                Some parts of our system will only consider the contents of enabled distro survey files.
                For example, a subsurvey can only be viewed by applicants if it is in an enabled survey.
                Distro Editor allows you to create additional surveys beyond "entireprogram,"
                but these are not enabled by default.
              </p>
              <p className="text-muted mb-3">
                Previously, surveys were "enabled" by adding them as "Import" "Questions"
                in the Airtable definition for the program. This page is a replacement for that.
              </p>
            </Card>
          </div>

          {/* Edit/Add Survey Modal */}
          <Modal
            open={showEditModal}
            onClose={closeEditModal}
            title={editingSurvey ? 'Edit Survey' : 'Enable Survey'}
            size="md"
          >
            <div className="py-3">
              <Form.Group className="mb-3">
                <Form.Label>
                  Survey Name <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.surveyName || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    setFormData({ ...formData, surveyName: e.target.value })
                  }
                  placeholder="e.g., entireprogram"
                  disabled={!!editingSurvey}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  URL <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.url || ''}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                    setFormData({ ...formData, url: e.target.value })
                  }
                  placeholder="e.g., dso-postgres-entireprogram"
                />
                <Form.Text className="text-muted">
                  Format: dso-&lt;program&gt;-&lt;surveyName&gt;
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>
                  Kind <span className="text-danger">*</span>
                </Form.Label>
                <Form.Control
                  as="select"
                  value={formData.kind || ''}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => 
                    setFormData({ ...formData, kind: e.target.value as 'inline' | 'subsurvey' })
                  }
                >
                  <option value="">Select kind...</option>
                  <option value="inline">Inline</option>
                  <option value="subsurvey">Subsurvey</option>
                </Form.Control>
              </Form.Group>

              {formData.kind === 'subsurvey' && (
                <Form.Group className="mb-3">
                  <Form.Label>Path</Form.Label>
                  <Form.Control
                    type="text"
                    value={formData.path || ''}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => 
                      setFormData({ ...formData, path: e.target.value })
                    }
                    placeholder="Subsurvey path"
                  />
                  <Form.Text className="text-muted">
                    Required for subsurvey kind
                  </Form.Text>
                </Form.Group>
              )}

              <Form.Group className="mb-4">
                <Form.Label>Needs Key</Form.Label>
                <Form.Control
                  as="select"
                  value={formData.needs_key === undefined ? 'undefined' : String(formData.needs_key)}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                    const value = e.target.value;
                    setFormData({ 
                      ...formData, 
                      needs_key: value === 'undefined' ? undefined : value === 'true'
                    });
                  }}
                >
                  <option value="undefined">Undefined</option>
                  <option value="true">True</option>
                  <option value="false">False</option>
                </Form.Control>
                <Form.Text className="text-muted">
                  Whether this survey needs a key for access
                </Form.Text>
              </Form.Group>

              <div className="d-flex justify-content-end pt-3 border-top" style={{ gap: '0.5rem' }}>
                <Button
                  variant="secondary"
                  onClick={closeEditModal}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSaveFromModal}
                  disabled={saving}
                >
                  {saving ? <Spinner size="small" /> : editingSurvey ? 'Save Changes' : 'Enable Survey'}
                </Button>
              </div>
            </div>
          </Modal>
        </div>
      </div>
    </div>
  );
};
